import * as wjcCore from "@grapecity/wijmo"
import * as wjcGrid from "@grapecity/wijmo.grid"
import * as wjrGrid from "@grapecity/wijmo.react.grid"
import * as wjRGridFilter from "@grapecity/wijmo.react.grid.filter"
import dayjs from "dayjs"
import { useCallback, useEffect, useRef } from "react"
import CONSTANT from "../../../constant"
import { T車検点検免許 } from "../../../service/dashboard/TDashboard"

import { TWijmoCellTemplateContext } from "../../../type/wijmoComponents"
import { Badge } from "react-bootstrap"
import util from "../../../ts/util"
import { メニューコード } from "../../../constant/menuCode"
import _ from "lodash"
import { useSelector } from "react-redux"
import { RootState } from "../../../globalStore/rootreducer"

const createBadgeBg = (区分: string) => {
  switch (区分) {
    case "車検":
      return "danger"
    case "点検":
      return "primary"
    case "免許":
      return "success"

    default:
      return "primary"
  }
}

export const SyakenTenkenMenkyoGrid: React.FC<{
  syakenTenkenMenkyoList: T車検点検免許[] | null | undefined
  syakenFlag: boolean
  tenkenFlag: boolean
  menkyoFlag: boolean
  busyoCode: string | null | undefined
}> = (props) => {
  const gridRef = useRef<wjrGrid.FlexGrid>(null)
  const 営業日 = useSelector((root: RootState) => root.date.eigyobiStr)
  const 年 = wjcCore.isNullOrWhiteSpace(営業日)
    ? dayjs().year()
    : dayjs(営業日).year()
  const 年月 = wjcCore.isNullOrWhiteSpace(営業日)
    ? dayjs().format("YYYY/01/01")
    : dayjs(営業日).format("YYYY/01/01")

  const initializedHandler = useCallback((flexGrid: wjcGrid.FlexGrid) => {
    flexGrid.formatItem.addHandler(
      (grid: wjcGrid.FlexGrid, e: wjcGrid.FormatItemEventArgs) => {
        if (e.panel === grid.cells) {
          const col = grid.getColumn(e.col)

          const 残り期日 = grid.getCellData(e.row, "残り期日", false)

          switch (col.binding as keyof T車検点検免許) {
            case "残り期日":
            case "期日":
            case "実施予定日":
              if (残り期日 != null) {
                wjcCore.toggleClass(e.cell, "fw-bold", 残り期日 <= 0)
                wjcCore.toggleClass(e.cell, "text-danger", 残り期日 < 0)
              } else {
                // 残り期日が空の場合、実施予定日で判断する
                const 実施予定日: Date = grid.getCellData(
                  e.row,
                  "実施予定日",
                  false
                )
                const 実施予定日まで = dayjs(実施予定日).diff(dayjs(), "day")

                wjcCore.toggleClass(e.cell, "fw-bold", 実施予定日まで <= 0)
                wjcCore.toggleClass(e.cell, "text-danger", 実施予定日まで < 0)
              }
              break

            default:
              break
          }
        }
      }
    )
  }, [])

  useEffect(() => {
    if (
      gridRef == null ||
      gridRef.current == null ||
      gridRef.current.control == null ||
      props.syakenTenkenMenkyoList == null
    )
      return

    const grid = gridRef.current.control as wjcGrid.FlexGrid

    const createItemsSource = (
      item: T車検点検免許
    ): { [name in keyof T車検点検免許]: any } => {
      return {
        ...item,
        前回更新日: dayjs(item.前回更新日).isValid()
          ? dayjs(item.前回更新日).toDate()
          : undefined,
        期日: dayjs(item.期日).isValid()
          ? dayjs(item.期日).toDate()
          : undefined,
        実施予定日: dayjs(item.実施予定日).isValid()
          ? dayjs(item.実施予定日).toDate()
          : undefined,
      }
    }
    const 表示区分: string[] = []
    if (props.syakenFlag) {
      表示区分.push("車検")
    }
    if (props.tenkenFlag) {
      表示区分.push("点検")
    }
    if (props.menkyoFlag) {
      表示区分.push("免許")
    }

    // 部所選択時は絞り込む
    const newItemsSource = props.syakenTenkenMenkyoList
      .filter((item) =>
        !wjcCore.isNullOrWhiteSpace(props.busyoCode ?? "")
          ? item.部所コード === props.busyoCode
          : true
      )
      .filter((item) => 表示区分.some((e) => e === item.車検点検免許表示区分))
      .map(createItemsSource)

    grid.itemsSource = _.orderBy(
      newItemsSource,
      ["車検点検免許表示区分", "期日", "実施予定日"],
      ["desc"]
    )
  }, [
    props.syakenTenkenMenkyoList,
    props.busyoCode,
    props.syakenFlag,
    props.tenkenFlag,
    props.menkyoFlag,
  ])

  return (
    <wjrGrid.FlexGrid
      className="max-h-18rem"
      {...CONSTANT.GRID.PROPERTY_SET.FLEX_GRID_PROPS}
      ref={gridRef}
      initialized={initializedHandler}>
      <wjRGridFilter.FlexGridFilter />
      <wjrGrid.FlexGridColumn width={60} binding={"車番"} header={"車番"}>
        <wjrGrid.FlexGridCellTemplate
          cellType={wjrGrid.CellTemplateType.Cell}
          template={(context: TWijmoCellTemplateContext<T車検点検免許>) => {
            // 車両点検整備一覧に遷移
            let url = util.getMenuPath(メニューコード.車輌管理.点検整備一覧)[0]
            // 検索を行うためクエリ追加
            url += `?年月=${年月}&年=${年}&車番=${context.item.車番}&searched=1`
            // 今のところ全社員閲覧可能なためURLは常に出す
            return <a href={url}>{context.item.車番}</a>
          }}
        />
      </wjrGrid.FlexGridColumn>
      <wjrGrid.FlexGridColumn
        width={150}
        binding={"登録番号"}
        header={"登録番号"}></wjrGrid.FlexGridColumn>
      <wjrGrid.FlexGridColumn
        width={80}
        binding={"車検点検免許表示区分"}
        header={"区分"}>
        <wjrGrid.FlexGridCellTemplate
          cellType={wjrGrid.CellTemplateType.Cell}
          template={(context: TWijmoCellTemplateContext<T車検点検免許>) => {
            return (
              <Badge bg={createBadgeBg(context.item.車検点検免許表示区分)}>
                {context.item.車検点検免許表示区分}
              </Badge>
            )
          }}
        />
      </wjrGrid.FlexGridColumn>
      <wjrGrid.FlexGridColumn
        width={120}
        binding={"社員氏名"}
        header={"乗務員"}>
        <wjrGrid.FlexGridCellTemplate
          cellType={wjrGrid.CellTemplateType.Cell}
          template={(context: TWijmoCellTemplateContext<T車検点検免許>) => {
            if (context.item.車検点検免許表示区分 !== "免許")
              return <>{context.item.社員氏名}</>

            const url = util
              .getMenuPath(メニューコード.乗務員管理.乗務員マスタ登録)[0]
              .replaceAll(":id", `${context.item.乗務員ID}`)
            // 今のところ全社員閲覧可能なためURLは常に出す

            return <a href={url}>{context.item.社員氏名}</a>
          }}
        />
      </wjrGrid.FlexGridColumn>
      <wjrGrid.FlexGridColumn binding={"期日"}></wjrGrid.FlexGridColumn>
      <wjrGrid.FlexGridColumn
        binding={"残り期日"}
        header={"あと何日"}
        dataType={wjcCore.DataType.Number}></wjrGrid.FlexGridColumn>
      <wjrGrid.FlexGridColumn binding={"実施予定日"}></wjrGrid.FlexGridColumn>
    </wjrGrid.FlexGrid>
  )
}
